// src/marketing/content/ContentBoard.tsx
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useState } from 'react'
import type { Content, ContentStatus } from '../../lib/types'
import { ContentCard } from './ContentCard'
import { CONTENT_STATUSES, CONTENT_STATUS_LABELS, CONTENT_STATUS_TEST_IDS } from './statuses'

interface ContentBoardProps {
  contents: Content[]
  onStatusChange: (contentId: string, status: ContentStatus) => void
  onOpen: (contentId: string) => void
  isPatchPending: boolean
}

export function ContentBoard({ contents, onStatusChange, onOpen, isPatchPending }: ContentBoardProps) {
  const [activeContent, setActiveContent] = useState<Content | null>(null)

  // activationConstraint distance 5 : seul garde-fou clic/drag du repo (PipelineBoard).
  // C'est lui qui rend le onClick de ContentCard utilisable.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  function handleDragStart(event: DragStartEvent) {
    setActiveContent(contents.find((c) => c.id === event.active.id) ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveContent(null)
    const { active, over } = event
    if (!over) return
    const contentId = active.id as string
    const targetStatus = over.id as ContentStatus
    const content = contents.find((c) => c.id === contentId)
    if (!content || content.status === targetStatus) return
    if (!CONTENT_STATUSES.includes(targetStatus)) return
    onStatusChange(contentId, targetStatus)
  }

  const byStatus = CONTENT_STATUSES.reduce<Record<ContentStatus, Content[]>>(
    (acc, s) => {
      acc[s] = contents.filter((c) => c.status === s)
      return acc
    },
    {} as Record<ContentStatus, Content[]>,
  )

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div
        data-testid="content-board"
        style={{
          display: 'flex',
          gap: '0.75rem',
          overflowX: 'auto',
          paddingBottom: '1rem',
          minHeight: '60vh',
        }}
      >
        {CONTENT_STATUSES.map((status) => (
          <ContentColumn
            key={status}
            status={status}
            contents={byStatus[status]}
            onOpen={onOpen}
            isPatchPending={isPatchPending}
          />
        ))}
      </div>
      <DragOverlay>
        {activeContent ? (
          <div style={{
            background: 'var(--elevated)',
            border: '1px solid var(--arancio)',
            borderRadius: '8px',
            padding: '0.75rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            width: '220px',
          }}>
            <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--fg)' }}>
              {activeContent.title}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

interface ContentColumnProps {
  status: ContentStatus
  contents: Content[]
  onOpen: (contentId: string) => void
  isPatchPending: boolean
}

function ContentColumn({ status, contents, onOpen, isPatchPending }: ContentColumnProps) {
  const { setNodeRef: setDropRef } = useDroppable({ id: status })

  return (
    <div
      data-testid={CONTENT_STATUS_TEST_IDS[status]}
      data-column={status}
      style={{
        minWidth: '220px',
        width: '220px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--line)',
      }}>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          flex: 1,
        }}>
          {CONTENT_STATUS_LABELS[status]}
        </span>
        {contents.length > 0 && (
          <span style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--dim)',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '4px',
            padding: '0.0625rem 0.3125rem',
          }}>
            {contents.length}
          </span>
        )}
      </div>

      {/* La zone de dépôt est un div dédié avec minHeight — sans ça une colonne vide
          (« Archivé ») n'offre aucune surface de drop. Repris de PipelineColumn. */}
      <SortableContext id={status} items={contents.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div
          ref={setDropRef}
          data-droppable={status}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            minHeight: '80px',
            padding: '0.25rem',
            borderRadius: '8px',
            background: contents.length === 0 ? 'var(--elevated)' : 'transparent',
            border: contents.length === 0 ? '1px dashed var(--line)' : 'none',
            opacity: isPatchPending ? 0.7 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {contents.map((c) => (
            <ContentCard key={c.id} content={c} onOpen={onOpen} />
          ))}
        </div>
      </SortableContext>
    </div>
  )
}
